import { Inject, Injectable } from '@angular/core';
import { ApiBaseService } from 'libs/shared-services/src/lib/api-base.service';
import { LocaleService } from 'libs/shared-services/src/lib/locale.service';
import { ToasterService } from 'libs/shared-services/src/lib/toaster.service';
import { Observable, Subscription, finalize, map } from 'rxjs';

import { RestaurantDashboardStateService } from './data-store/restaurant-dashboard.state';
import {
  DashboardDailyResponse,
  DashboardOrderChannelResponse,
  DashboardOrderResponse,
  DashboardProductsResponse,
  DashboardRatingDistributionResponse,
  DashboardRecentReviewResponse,
  DashboardReviewsResponse,
  DashboardSummaryResponse,
  DashboardTopProductResponse,
  RestaurantDashboardResponse,
} from './data-store/model/restaurant-dashboard-response';

@Injectable({
  providedIn: 'root',
})
export class RestaurantDashboardService {
  public static readonly DEFAULT_PERIOD_DAYS: number = 7;
  public static readonly AVAILABLE_PERIOD_DAYS: number[] = [7, 30];

  private requestSubscription: Subscription | null = null;

  public constructor(
    @Inject('env') private readonly environment: any,
    private readonly apiService: ApiBaseService,
    private readonly toasterService: ToasterService,
    private readonly localeService: LocaleService,
    private readonly dashboardState: RestaurantDashboardStateService
  ) {}

  public fetchDashboardAPI(periodDays: number = RestaurantDashboardService.DEFAULT_PERIOD_DAYS): void {
    const selectedPeriodDays = this.normalizePeriodDays(periodDays);

    this.requestSubscription?.unsubscribe();
    if (selectedPeriodDays !== this.dashboardState.getSelectedPeriodDays()) {
      this.dashboardState.setData(null);
    }
    this.dashboardState.setSelectedPeriodDays(selectedPeriodDays);
    this.dashboardState.setError(false);
    this.dashboardState.setLoading(true);

    const url = `${this.environment.API_GET_RESTAURANT_DASHBOARD}?periodDays=${selectedPeriodDays}`;
    this.requestSubscription = this.apiService
      .get(url)
      .pipe(
        map((response: any) => this.mapDashboardResponse(response)),
        finalize(() => this.dashboardState.setLoading(false))
      )
      .subscribe({
        next: (dashboard: RestaurantDashboardResponse) => {
          this.dashboardState.setData(dashboard);
        },
        error: () => {
          this.dashboardState.setError(true);
          this.toasterService.showError(
            this.localeService.translate('dashboard_error_title'),
            this.localeService.translate('dashboard_error_description')
          );
        },
      });
  }

  public retry(): void {
    this.fetchDashboardAPI(this.dashboardState.getSelectedPeriodDays());
  }

  public dashboard$(): Observable<RestaurantDashboardResponse | null> {
    return this.dashboardState.getData$();
  }

  public isLoading$(): Observable<boolean> {
    return this.dashboardState.isLoading$();
  }

  public hasError$(): Observable<boolean> {
    return this.dashboardState.hasError$();
  }

  public selectedPeriodDays$(): Observable<number> {
    return this.dashboardState.selectedPeriodDays$();
  }

  private normalizePeriodDays(periodDays: number): number {
    return RestaurantDashboardService.AVAILABLE_PERIOD_DAYS.includes(periodDays)
      ? periodDays
      : RestaurantDashboardService.DEFAULT_PERIOD_DAYS;
  }

  private mapDashboardResponse(response: any): RestaurantDashboardResponse {
    const dashboard = Object.assign(new RestaurantDashboardResponse(), response || {});

    dashboard.today = Object.assign(new DashboardSummaryResponse(), response?.today || {});
    dashboard.currentPeriod = Object.assign(
      new DashboardSummaryResponse(),
      response?.currentPeriod || {}
    );
    dashboard.previousPeriod = Object.assign(
      new DashboardSummaryResponse(),
      response?.previousPeriod || {}
    );
    dashboard.dailySeries = (response?.dailySeries || []).map((item: any) =>
      Object.assign(new DashboardDailyResponse(), item)
    );
    dashboard.actionRequiredOrders = (response?.actionRequiredOrders || []).map((item: any) =>
      Object.assign(new DashboardOrderResponse(), item)
    );
    dashboard.activeOrders = (response?.activeOrders || []).map((item: any) =>
      Object.assign(new DashboardOrderResponse(), item)
    );
    dashboard.orderChannelBreakdown = (response?.orderChannelBreakdown || []).map((item: any) =>
      Object.assign(new DashboardOrderChannelResponse(), item)
    );
    dashboard.topProducts = (response?.topProducts || []).map((item: any) =>
      Object.assign(new DashboardTopProductResponse(), item)
    );
    dashboard.reviews = Object.assign(new DashboardReviewsResponse(), response?.reviews || {});
    dashboard.reviews.ratingDistribution = (response?.reviews?.ratingDistribution || []).map(
      (item: any) => Object.assign(new DashboardRatingDistributionResponse(), item)
    );
    dashboard.reviews.recentReviews = (response?.reviews?.recentReviews || []).map((item: any) =>
      Object.assign(new DashboardRecentReviewResponse(), item)
    );
    dashboard.products = Object.assign(new DashboardProductsResponse(), response?.products || {});

    return dashboard;
  }
}
